import { useState, type MouseEvent } from 'react';
import {
  alpha,
  Badge,
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import { releaseNotes } from '@/config/releaseNotes';
import { useWhatsNewUnread } from './useWhatsNewUnread';
import { getWhatsNewPopoverOrigins } from './getWhatsNewPopoverOrigins';
import { WhatsNewPopoverContent } from './WhatsNewPopoverContent';

export interface WhatsNewRowProps {
  /** Drawer mini-variant — icon-only trigger, label as hover tooltip. */
  readonly collapsed?: boolean;
}

/**
 * Sidebar-footer "What's New" row — mounted in `AppShell.tsx` directly
 * above `ThemeToggle`, between the nav-section `Divider` and the theme
 * row (requirements-whats-new.md §4). Modelled on `SidebarPersonaCard.tsx`,
 * not `ThemeToggle.tsx`: this trigger owns an open popover, so — per the
 * same hazard `SidebarPersonaCard`'s own docstring documents — the root
 * trigger element MUST be the same JSX branch regardless of `collapsed`,
 * with only the inner label/version text conditionally omitted. Giving
 * collapsed/expanded two structurally different trees would remount the
 * trigger (and drop the Popover's `anchorEl`) on every sidebar-rail
 * toggle performed while this popover is open (Swarm arbitration, WNB-3:
 * UI/UX + State Management both flagged this as the nearest, and wrong,
 * precedent to copy from `ThemeToggle`).
 *
 * Click toggles the popover open/closed (a deliberate, reviewed deviation
 * from `SidebarPersonaCard`'s "click always (re)opens" — Menu's own
 * `onClose` already handles outside-click/Escape; toggling on a second
 * click of the bell itself is an additional, intentional shortcut for a
 * dismissible announcement panel).
 */
export const WhatsNewRow = ({ collapsed = false }: WhatsNewRowProps): JSX.Element => {
  const { unread, markSeen } = useWhatsNewUnread();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const currentVersion = releaseNotes[0]?.version;
  // Active only while actually on /updates — not while the popover happens
  // to be open, which is reachable from every page and isn't a completed
  // navigation (2026-10 polish; matches `NavItem`'s own route-match-based,
  // not transient-UI-state-based, active semantics).
  const isActive = useLocation().pathname === '/updates';

  const handleToggle = (event: MouseEvent<HTMLElement>): void => {
    if (anchor) {
      setAnchor(null);
      return;
    }
    setAnchor(event.currentTarget);
    markSeen();
  };

  const handleClose = (): void => setAnchor(null);

  const { anchorOrigin, transformOrigin } = getWhatsNewPopoverOrigins();

  return (
    <Box sx={{ px: collapsed ? 0.5 : 1 }}>
      <Tooltip title={collapsed ? "What's New" : ''} placement="right" arrow>
        <ButtonBase
          onClick={handleToggle}
          aria-label="What's New"
          aria-haspopup="true"
          aria-expanded={Boolean(anchor)}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: collapsed ? 0 : 1.5,
            py: 1,
            borderRadius: 1,
            justifyContent: collapsed ? 'center' : 'flex-start',
            // Matches NavItem.tsx's `.active` treatment: a light-blue tint +
            // blue ink, applied as plain conditional sx values on this same
            // ButtonBase — never a second JSX branch (see this file's own
            // docstring on why the root trigger element must stay stable).
            bgcolor: isActive ? (theme) => alpha(theme.palette.primary.main, 0.08) : undefined,
            color: isActive ? 'primary.main' : 'text.secondary',
            fontWeight: isActive ? 600 : undefined,
            '&:hover': { bgcolor: isActive ? undefined : 'action.hover', color: isActive ? undefined : 'text.primary' },
          }}
        >
          <Badge
            variant="dot"
            color="error"
            invisible={!unread}
            overlap="circular"
            sx={{ flexShrink: 0 }}
          >
            <CampaignIcon fontSize="small" />
          </Badge>
          {!collapsed ? (
            <>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                What&apos;s New
              </Typography>
              {currentVersion ? (
                <Typography
                  variant="caption"
                  noWrap
                  color="text.secondary"
                  sx={{ ml: 'auto', flexShrink: 0 }}
                >
                  v{currentVersion}
                </Typography>
              ) : null}
            </>
          ) : null}
        </ButtonBase>
      </Tooltip>
      <Popover
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          paper: {
            // `overflow: hidden` is load-bearing: the Popover's own Paper
            // already auto-scrolls to stay within the viewport, so without
            // this, a tall body (WNB-4's scrollable content) produces TWO
            // nested scroll containers — the Paper and the content Box
            // both scrolling (Swarm review, WNB-4: UI/UX Consistency).
            // Only WhatsNewPopoverContent's inner Box should ever scroll.
            sx: { width: 380, maxWidth: 'calc(100vw - 32px)', overflow: 'hidden' },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            What&apos;s New
          </Typography>
          <IconButton size="small" aria-label="Close" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        <WhatsNewPopoverContent releases={releaseNotes} onNavigate={handleClose} />
      </Popover>
    </Box>
  );
};
